import { createContext, FC, useContext, useState } from 'react';

import { Button, Preloader } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { useGameAccounts } from 'hooks';
import { LocalizationContext } from 'localization';
import { TGameAccountsGroup } from 'types';

import s from './GameAccounts.module.scss';
import { GameAccountsTable } from './GameAccountsTable';
import { IGameAccountsContext } from './interfaces';
import { ManageGameAccountMW } from './ManageGameAccountMW';
import { ManageGameAccountsGroupMW } from './ManageGameAccountsGroupMW';

export const GameAccountsContext = createContext<IGameAccountsContext | null>(
  null
);

export const GameAccounts: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [isManageAccountsGroupMWOpen, setIsManageAccountsGroupMWOpen] =
    useState(false);
  const openAccountsGroupManager = (): void =>
    setIsManageAccountsGroupMWOpen(true);

  const [manageAccountInGroup, setManageAccountInGroup] =
    useState<TGameAccountsGroup | null>(null);
  const openAccountManager = (group: TGameAccountsGroup): void =>
    setManageAccountInGroup(group);

  const isAvailable = useIsFeatureAvailable('game_accounts');
  const {
    accountsGroups,
    loading,
    accountsGroupForEdit,
    setAccountsGroupForEdit,
    accountForEdit,
    setAccountForEdit,
    createAccountsGroup,
    updateAccountsGroup,
    deleteAccountsGroup,
    createAccount,
    updateAccount,
    deleteAccount,
    freeAccount,
  } = useGameAccounts(permissions);

  return (
    <GameAccountsContext.Provider
      value={{
        openAccountsGroupManager,
        openAccountManager,
        accountsGroupForEdit,
        setAccountsGroupForEdit,
        accountForEdit,
        setAccountForEdit,
        createAccountsGroup,
        updateAccountsGroup,
        deleteAccountsGroup,
        createAccount,
        updateAccount,
        deleteAccount,
        freeAccount,
      }}
    >
      {isManageAccountsGroupMWOpen && (
        <ManageGameAccountsGroupMW
          onClose={() => {
            setIsManageAccountsGroupMWOpen(false);
            setAccountsGroupForEdit(null);
          }}
        />
      )}
      {manageAccountInGroup && (
        <ManageGameAccountMW
          group={manageAccountInGroup}
          onClose={() => {
            setAccountForEdit(null);
            setManageAccountInGroup(null);
          }}
        />
      )}
      <div className={s['page-container']}>
        <div className={s.controls}>
          <FeatureHOC
            alias="game_accounts"
            strategy="disable"
            tooltipPosition="bottom"
            name={t('nav.gameAccounts')}
          >
            <Button
              iconName="CirclePlusIcon"
              onClick={openAccountsGroupManager}
              disabled={!isAvailable}
              className={s.button}
            >
              {t('applications.groups.create.add')}
            </Button>
          </FeatureHOC>
        </div>
        <Preloader
          loading={loading}
          hasData={accountsGroups.length > 0}
          noDataPlaceholder={t('accounts.groups.placeholder')}
        >
          {accountsGroups.map((group) => (
            <GameAccountsTable
              key={`game-accounts-group-${group.id}`}
              group={group}
            />
          ))}
        </Preloader>
      </div>
    </GameAccountsContext.Provider>
  );
};
